import { useEffect, useMemo, useState } from "react";
import type { Business, BusinessSort, DirectoryLocation } from "@/types/directory";
import { queryBusinesses } from "@/services/directoryService";
import DistrictMap from "./DistrictMap";
import TalukList from "./TalukList";
import RatingFilter from "./RatingFilter";
import DistrictBusinessList from "./DistrictBusinessList";
import PromoBand from "./PromoBand";
import TouristSpots from "./TouristSpots";
import { useDistrictPromos } from "@/hooks/useDistrictPromos";

interface DistrictViewProps {
  district: DirectoryLocation;
  taluks: DirectoryLocation[];
}

const TOP_LIMIT = 20;
/** Fetch a wider pool so the rating filter can still fill a full top-20 (API caps limit at 50). */
const FETCH_LIMIT = 50;

export default function DistrictView({ district, taluks }: DistrictViewProps) {
  const [selectedTaluk, setSelectedTaluk] = useState("");
  const [minRating, setMinRating] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState<BusinessSort>("rating");
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);

  const { sponsored, featured, loading: promoLoading } = useDistrictPromos(district.slug);

  useEffect(() => {
    let active = true;
    setLoading(true);
    queryBusinesses({
      district: district.slug,
      location: selectedTaluk || undefined,
      keyword: keyword.trim() || undefined,
      sort,
      page: 1,
      pageSize: FETCH_LIMIT,
    })
      .then((response) => {
        if (active) setBusinesses(response.items);
      })
      .catch(() => {
        if (active) setBusinesses([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [district.slug, selectedTaluk, keyword, sort]);

  const visible = useMemo(() => {
    const filtered =
      minRating > 0 ? businesses.filter((b) => b.rating >= minRating) : businesses;
    return filtered.slice(0, TOP_LIMIT);
  }, [businesses, minRating]);

  const selectedTalukName = taluks.find((taluk) => taluk.slug === selectedTaluk)?.name;

  return (
    <>
      <section className="mx-auto w-full max-w-[1400px] px-4 pt-8 md:px-6 md:pt-10">
        <DistrictMap
          districtName={district.name}
          talukCount={taluks.length}
          businessCount={district.businessCount}
        />
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_2fr]">
          <aside className="space-y-5 lg:sticky lg:top-[88px] lg:self-start">
            <TalukList
              districtName={district.name}
              taluks={taluks}
              selected={selectedTaluk}
              onSelect={setSelectedTaluk}
            />
            <RatingFilter value={minRating} onChange={setMinRating} />
            <TouristSpots districtSlug={district.slug} districtName={district.name} />
          </aside>

          <div>
            <PromoBand
              sponsored={sponsored}
              featured={featured}
              loading={promoLoading}
            />

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <span className="pointer-events-none absolute left-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                    <i className="ri-search-line text-base" />
                  </span>
                  <input
                    type="text"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder={`Search businesses in ${selectedTalukName ?? district.name}...`}
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value as BusinessSort)}
                  aria-label="Sort listings"
                  className="h-11 cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 sm:w-48"
                >
                  <option value="rating">Highest rated</option>
                  <option value="relevance">Relevance</option>
                  <option value="newest">Newest first</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                Top listings {selectedTalukName ? `in ${selectedTalukName}` : `across ${district.name}`}
              </h2>
              <p className="text-sm text-foreground-600">
                {loading
                  ? "Loading..."
                  : `${visible.length} of top ${TOP_LIMIT} listings`}
              </p>
            </div>

            <DistrictBusinessList businesses={visible} loading={loading} />
          </div>
        </div>
      </section>
    </>
  );
}